import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { FaArrowRight, FaTrash, FaSearchPlus, FaSearchMinus, FaCompress, FaMousePointer } from 'react-icons/fa';
import * as pdfjsLib from 'pdfjs-dist';
import { getApiEndpoint } from '../../../../config/api';
import { getAuthHeader } from '../../../../utils/auth';
import { savePins } from '../../../../services/onboardingWizardService';

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;

const MOVE_THRESHOLD = 5; // px before a mousedown becomes a marquee (vs. click-to-drop)

function Step2DropPins({ session, propertyId, pins: initialPins, onApplyBundle, onAdvance, onFlush }) {
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const saveTimer = useRef(null);

  const [imageSrc, setImageSrc] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [frameSize, setFrameSize] = useState(null);

  // Refs so the non-passive wheel handler always reads current state
  const scaleRef = useRef(scale);
  const offsetRef = useRef(offset);
  useEffect(() => { scaleRef.current = scale; }, [scale]);
  useEffect(() => { offsetRef.current = offset; }, [offset]);

  // Size the frame to match the image's actual rendered pixels, so pin % and click %
  // are measured against the same box.
  const recalcFrameSize = useCallback(() => {
    const img = imgRef.current;
    const vp = containerRef.current;
    if (!img || !vp || !img.naturalWidth) return;
    const padding = 24;
    const maxW = vp.clientWidth - padding * 2;
    const maxH = vp.clientHeight - padding * 2;
    const aspect = img.naturalWidth / img.naturalHeight;
    let w = maxW;
    let h = w / aspect;
    if (h > maxH) { h = maxH; w = h * aspect; }
    setFrameSize({ w, h });
  }, []);

  useEffect(() => {
    window.addEventListener('resize', recalcFrameSize);
    return () => window.removeEventListener('resize', recalcFrameSize);
  }, [recalcFrameSize]);

  const [pins, setPins] = useState(() => normalisePins(initialPins));
  const [selected, setSelected] = useState(() => new Set());

  const [drag, setDrag] = useState(null);
  // drag shapes:
  //   { type: 'pan', origin: {x,y}, startOffset: {x,y} }
  //   { type: 'pin', id, startPercent: {x%,y%}, origin: {x,y} }
  //   { type: 'pending', origin: {x,y}, percent: {x%,y%}, moved: false, additive: bool }
  //   { type: 'marquee', startPct: {x%,y%}, endPct: {x%,y%}, additive: bool }

  // ——— Load plan as image via pdfjs ———
  useEffect(() => {
    if (!session.document_id) return;
    let cancelled = false;
    (async () => {
      try {
        const url = getApiEndpoint(`/api/properties/${propertyId}/documents/${session.document_id}/download`);
        const res = await fetch(url, { headers: { ...getAuthHeader() } });
        if (!res.ok) throw new Error(`Download failed (${res.status})`);
        const blob = await res.blob();
        const bytes = await blob.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: new Uint8Array(bytes), isEvalSupported: false }).promise;
        const page = await pdf.getPage(1);
        const viewport = page.getViewport({ scale: 2 });
        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext('2d');
        await page.render({ canvasContext: ctx, viewport }).promise;
        if (cancelled) return;
        setImageSrc(canvas.toDataURL('image/png'));
      } catch (e) {
        if (!cancelled) setLoadError(e.message || 'Could not render plan');
      }
    })();
    return () => { cancelled = true; };
  }, [session.document_id, propertyId]);

  // ——— Sync pins only when the session itself changes (not after every auto-save) ———
  // Syncing on initialPins.length caused a race: the debounce could fire mid-edit,
  // onApplyBundle would update the parent, which changed initialPins, which wiped
  // any locally-added pins that hadn't been included in that save batch yet.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPins(normalisePins(initialPins)); }, [session.id]);

  // ——— Debounced save ———
  // Do NOT call onApplyBundle here — that would push server pins back into the parent,
  // flow down as initialPins, and overwrite pins the user placed after this batch was sent.
  // The final save in continueToCategorise handles the parent update before advancing.
  const queueSave = useCallback((next) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await savePins(session.id, next);
      } catch (e) { toast.error(e.message || 'Pin save failed'); }
    }, 500);
  }, [session.id]);

  const updatePins = (next) => { setPins(next); queueSave(next); };

  // ——— Helpers: coordinate conversion ———
  const getPercentAt = (clientX, clientY) => {
    const el = imgRef.current; if (!el) return null;
    const rect = el.getBoundingClientRect();
    const x = ((clientX - rect.left) / rect.width) * 100;
    const y = ((clientY - rect.top) / rect.height) * 100;
    return { x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) };
  };

  // ——— Input handlers ———
  const onMouseDownPlan = (e) => {
    if (e.button === 1 || e.altKey) {
      // middle-mouse or alt: pan mode
      setDrag({ type: 'pan', origin: { x: e.clientX, y: e.clientY }, startOffset: offset });
      return;
    }
    if (e.shiftKey) {
      // shift+drag on empty area → marquee-select
      const pct = getPercentAt(e.clientX, e.clientY);
      if (!pct) return;
      setDrag({ type: 'pending-marquee', origin: { x: e.clientX, y: e.clientY }, percent: pct, additive: true });
      return;
    }
    const pct = getPercentAt(e.clientX, e.clientY);
    if (!pct) return;
    // Plain mousedown: pending → drop-on-release, or drag → pan
    setDrag({ type: 'pending-drop', origin: { x: e.clientX, y: e.clientY }, percent: pct, startOffset: offset });
  };

  const onMouseDownPin = (e, pin) => {
    e.stopPropagation();
    if (e.shiftKey) {
      // shift-click toggles selection — don't start drag
      setSelected(prev => {
        const next = new Set(prev);
        if (next.has(pin.id)) next.delete(pin.id); else next.add(pin.id);
        return next;
      });
      return;
    }
    // Replace selection with this pin (unless already in selection)
    setSelected(prev => prev.has(pin.id) ? prev : new Set([pin.id]));
    // Snapshot start positions for every pin currently (or about to be) in the drag set
    const driverSel = (selected.has(pin.id) ? selected : new Set([pin.id]));
    const startMap = {};
    pins.forEach(p => { if (driverSel.has(p.id)) startMap[p.id] = { x: p.x_percent, y: p.y_percent }; });
    setDrag({ type: 'pin', id: pin.id, startMap, origin: { x: e.clientX, y: e.clientY } });
  };

  const onMouseMove = (e) => {
    if (!drag) return;
    if (drag.type === 'pan') {
      setOffset({ x: drag.startOffset.x + (e.clientX - drag.origin.x), y: drag.startOffset.y + (e.clientY - drag.origin.y) });
      return;
    }
    if (drag.type === 'pending-drop') {
      const dx = e.clientX - drag.origin.x;
      const dy = e.clientY - drag.origin.y;
      if (Math.hypot(dx, dy) > MOVE_THRESHOLD) {
        // escalate to pan — consume the initial delta immediately for a smooth handoff
        setOffset({ x: drag.startOffset.x + dx, y: drag.startOffset.y + dy });
        setDrag({ type: 'pan', origin: drag.origin, startOffset: drag.startOffset });
      }
      return;
    }
    const pct = getPercentAt(e.clientX, e.clientY);
    if (!pct) return;

    if (drag.type === 'pending-marquee') {
      const dx = e.clientX - drag.origin.x;
      const dy = e.clientY - drag.origin.y;
      if (Math.hypot(dx, dy) > MOVE_THRESHOLD) {
        setDrag({ type: 'marquee', startPct: drag.percent, endPct: pct, additive: drag.additive });
      }
      return;
    }
    if (drag.type === 'marquee') {
      setDrag({ ...drag, endPct: pct });
      return;
    }
    if (drag.type === 'pin') {
      const rect = imgRef.current.getBoundingClientRect();
      const dxPct = ((e.clientX - drag.origin.x) / rect.width) * 100;
      const dyPct = ((e.clientY - drag.origin.y) / rect.height) * 100;
      setPins(prev => prev.map(p => {
        const start = drag.startMap[p.id];
        if (!start) return p;
        return {
          ...p,
          x_percent: Math.max(0, Math.min(100, start.x + dxPct)),
          y_percent: Math.max(0, Math.min(100, start.y + dyPct)),
        };
      }));
    }
  };

  const onMouseUp = (e) => {
    if (!drag) return;
    if (drag.type === 'pending-drop') {
      // pure click (no drag) → drop a new pin
      const next = [...pins, {
        id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        temp_number: nextTempNumber(pins),
        x_percent: drag.percent.x, y_percent: drag.percent.y,
        category_id: null, name_reference: null
      }];
      updatePins(next);
      setSelected(new Set());
    } else if (drag.type === 'pending-marquee') {
      // pure shift-click → clear selection
      setSelected(new Set());
    } else if (drag.type === 'marquee') {
      const [x1, x2] = [Math.min(drag.startPct.x, drag.endPct.x), Math.max(drag.startPct.x, drag.endPct.x)];
      const [y1, y2] = [Math.min(drag.startPct.y, drag.endPct.y), Math.max(drag.startPct.y, drag.endPct.y)];
      const hits = pins.filter(p => p.x_percent >= x1 && p.x_percent <= x2 && p.y_percent >= y1 && p.y_percent <= y2);
      setSelected(prev => {
        const next = drag.additive ? new Set(prev) : new Set();
        hits.forEach(p => next.add(p.id));
        return next;
      });
    } else if (drag.type === 'pin') {
      queueSave(pins); // persist the new positions
    }
    setDrag(null);
  };

  // Attach global mouseup so a release outside the container still resolves
  useEffect(() => {
    const up = (e) => onMouseUp(e);
    const mv = (e) => onMouseMove(e);
    window.addEventListener('mouseup', up);
    window.addEventListener('mousemove', mv);
    return () => { window.removeEventListener('mouseup', up); window.removeEventListener('mousemove', mv); };
  });

  // Scroll-wheel zoom (cursor-anchored). Non-passive so we can block page scroll.
  useEffect(() => {
    const vp = containerRef.current;
    if (!vp) return;
    const handleWheel = (e) => {
      e.preventDefault();
      const rect = vp.getBoundingClientRect();
      const cursorX = e.clientX - rect.left;
      const cursorY = e.clientY - rect.top;
      const cur = scaleRef.current;
      const off = offsetRef.current;
      const factor = e.deltaY < 0 ? 1.04 : 0.96;
      const newScale = Math.max(0.3, Math.min(4, cur * factor));
      const contentX = (cursorX - off.x) / cur;
      const contentY = (cursorY - off.y) / cur;
      setScale(newScale);
      setOffset({ x: cursorX - contentX * newScale, y: cursorY - contentY * newScale });
    };
    vp.addEventListener('wheel', handleWheel, { passive: false });
    return () => vp.removeEventListener('wheel', handleWheel);
  }, []);

  // Keyboard: Delete removes selected pins; Esc clears selection
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setSelected(new Set());
      if ((e.key === 'Delete' || e.key === 'Backspace') && selected.size) {
        e.preventDefault();
        const next = pins.filter(p => !selected.has(p.id));
        // Renumber temp_number sequentially so pin numbers stay tidy
        const renumbered = next.map((p, i) => ({ ...p, temp_number: i + 1 }));
        updatePins(renumbered);
        setSelected(new Set());
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected, pins, updatePins]);

  const zoomIn = () => setScale(s => Math.min(s + 0.25, 4));
  const zoomOut = () => setScale(s => Math.max(s - 0.25, 0.3));
  const reset = () => { setScale(1); setOffset({ x: 0, y: 0 }); };

  const clearAll = () => {
    if (!pins.length) return;
    if (!window.confirm(`Delete all ${pins.length} pins?`)) return;
    updatePins([]);
    setSelected(new Set());
  };

  const continueToCategorise = async () => {
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
    if (!pins.length) { toast.error('Drop at least one pin first'); return; }
    try {
      // flush any pending saves + session step update
      await savePins(session.id, pins).then(onApplyBundle);
      await onFlush();
      onAdvance(3);
    } catch (e) { toast.error(e.message || 'Failed to continue'); }
  };

  // ——— Render ———
  if (!session.document_id) {
    return (
      <div className="ow-step-body">
        <section className="ow-card"><div className="ow-card-head">
          <div>
            <div className="ow-card-kicker">Step 2 · Drop pins</div>
            <h2 className="ow-card-title">Upload a plan first</h2>
            <p className="ow-card-sub">Return to Step 1 to add a floor-plan PDF before placing pins.</p>
          </div>
        </div></section>
      </div>
    );
  }

  const marqueeRect = drag?.type === 'marquee' ? {
    left: Math.min(drag.startPct.x, drag.endPct.x),
    top: Math.min(drag.startPct.y, drag.endPct.y),
    width: Math.abs(drag.endPct.x - drag.startPct.x),
    height: Math.abs(drag.endPct.y - drag.startPct.y),
  } : null;

  return (
    <div className="ow-step-body ow-step-wide">
      <section className="ow-card ow-card-tight">
        <header className="ow-card-head ow-plan-head">
          <div>
            <div className="ow-card-kicker">Step 2 · Drop pins</div>
            <h2 className="ow-card-title">Place a pin for every asset</h2>
            <p className="ow-card-sub ow-plan-hint">
              <strong>Click</strong> to drop · <strong>Drag empty area</strong> to pan · <strong>Scroll</strong> to zoom ·
              <strong> Drag pin</strong> to move · <strong>Shift+drag</strong> to marquee-select · <strong>Del</strong> to delete selected
            </p>
          </div>
          <div className="ow-plan-counters">
            <div className="ow-counter"><strong>{pins.length}</strong><em>pins</em></div>
            <div className="ow-counter ow-counter-alt"><strong>{selected.size}</strong><em>selected</em></div>
          </div>
        </header>

        <div className="ow-plan-toolbar">
          <div className="ow-plan-tools">
            <button className="ow-tool" onClick={zoomOut} title="Zoom out"><FaSearchMinus /></button>
            <span className="ow-zoom-readout">{Math.round(scale * 100)}%</span>
            <button className="ow-tool" onClick={zoomIn} title="Zoom in"><FaSearchPlus /></button>
            <button className="ow-tool" onClick={reset} title="Reset view"><FaCompress /></button>
            <span className="ow-tool-sep" />
            <button className="ow-tool" disabled={!selected.size} onClick={() => {
              const next = pins.filter(p => !selected.has(p.id)).map((p, i) => ({ ...p, temp_number: i + 1 }));
              updatePins(next); setSelected(new Set());
            }} title="Delete selected"><FaTrash /> <span>Delete selected</span></button>
            <button className="ow-tool" onClick={() => setSelected(new Set())} disabled={!selected.size} title="Clear selection"><FaMousePointer /> <span>Clear</span></button>
          </div>
          <button className="ow-btn ow-btn-muted" onClick={clearAll} disabled={!pins.length}>
            <FaTrash /> Clear all pins
          </button>
        </div>

        <div className="ow-plan-wrap">
          <div className="ow-plan-viewport" ref={containerRef} onMouseDown={onMouseDownPlan}>
            {loadError && <div className="ow-plan-error">{loadError}</div>}
            {!imageSrc && !loadError && <div className="ow-plan-loading">Rendering plan…</div>}
            {imageSrc && (
              <div
                className="ow-plan-stage"
                style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})` }}
              >
                <div className="ow-plan-frame" style={{
                  ...(frameSize ? { width: frameSize.w, height: frameSize.h } : {}),
                  '--pin-counter-scale': Math.max(0.25, 1 / Math.pow(scale, 1.25)),
                }}>
                  <img ref={imgRef} src={imageSrc} alt="Floor plan" className="ow-plan-image" draggable={false} onLoad={recalcFrameSize} />
                  {/* Pin layer — positioned relative to the image frame so % matches click measurements */}
                  {pins.map(pin => {
                    const isSel = selected.has(pin.id);
                    return (
                      <button
                        key={pin.id}
                        className={`ow-pin ${isSel ? 'is-selected' : ''}`}
                        style={{ left: `${pin.x_percent}%`, top: `${pin.y_percent}%` }}
                        onMouseDown={e => onMouseDownPin(e, pin)}
                        title={`Pin ${pin.temp_number}`}
                      >
                        <span className="ow-pin-body">{pin.temp_number}</span>
                      </button>
                    );
                  })}
                  {marqueeRect && (
                    <div className="ow-marquee" style={{
                      left: `${marqueeRect.left}%`, top: `${marqueeRect.top}%`,
                      width: `${marqueeRect.width}%`, height: `${marqueeRect.height}%`
                    }} />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <footer className="ow-card-foot">
          <span className="ow-foot-note">All pin moves auto-save.</span>
          <button className="ow-btn ow-btn-primary" onClick={continueToCategorise} disabled={!pins.length}>
            Continue to categorise <FaArrowRight />
          </button>
        </footer>
      </section>
    </div>
  );
}

function normalisePins(list) {
  return (list || []).map(p => ({
    id: p.id ?? `srv-${p.temp_number}`,
    temp_number: p.temp_number,
    x_percent: Number(p.x_percent),
    y_percent: Number(p.y_percent),
    category_id: p.category_id || null,
    name_reference: p.name_reference || null,
  }));
}
function nextTempNumber(pins) {
  const max = pins.reduce((m, p) => Math.max(m, p.temp_number || 0), 0);
  return max + 1;
}

export default Step2DropPins;
